// Main component that combines all parts
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useLocation } from 'react-router-dom';
import DiscussionLeftPanel from './Components/DiscussionLeftPanel';
import DiscussionRightPanel from './Components/DiscussionRightPanel';
import { DiscussionTopic, TopicWithCourse } from './types';
import { getCourses } from '../../../../../../Utils/apiService';
import { fetchDiscussionTopics } from '../../../Helper/DiscussionForumHelper';
import { getUserDetails } from '../../../../../../storageService';
import { CourseSf } from '../../../../../../interfaces/Common';
import KenLoader from '../../../../../../GlobalComponents/KenLoader';

// Sentinel for the Batch dropdown — a non-empty value so the Select shows
// "All batches" instead of an empty box. Batch keeps this "all" option/
// default; Training does not (it always resolves to one real training).
const ALL_BATCHES = 'all';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
}));

const FacultyPortalDiscussion: React.FC = () => {
  const classes = useStyles();
  const location = useLocation();
  const userDetails = getUserDetails();
  const currentUserName: string | undefined = userDetails?.Name || userDetails?.name;

  // Deep-links from a course page (e.g. "Discussion forum" on a course card)
  // pre-select that course's batch, and optionally a specific topic within
  // it; direct sidebar navigation has no such state and defaults to viewing
  // every batch with the first topic selected.
  const initialBatchId = location?.state?.batchId;
  const initialTopicId = location?.state?.topicId;

  const [courses, setCourses] = useState<CourseSf[]>([]);
  const [coursesLoaded, setCoursesLoaded] = useState(false);
  // Training (CourseName) narrows which batches the batch dropdown offers —
  // a faculty member teaching the same course across multiple batches
  // (terms) picks the training first, then the specific batch. Training has
  // no "all" option (always resolves to one real training, defaulting to
  // the deep-linked batch's training if any, else the first training).
  // Batch keeps "All batches" as an option and as its default, fanning the
  // topics fetch out across every batch of the selected training.
  const [selectedCourseName, setSelectedCourseName] = useState<string>('');
  const [selectedBatch, setSelectedBatch] = useState<string>(initialBatchId || ALL_BATCHES);

  const [topics, setTopics] = useState<TopicWithCourse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(initialTopicId || null);
  // A deep-linked topic should win over "select the first topic" exactly
  // once, on the load that follows mount — after that, normal batch
  // switches should go back to auto-selecting the first topic.
  const pendingDeepLinkTopicId = useRef<string | null>(initialTopicId || null);

  useEffect(() => {
    (async () => {
      try {
        const courseData: CourseSf[] = await getCourses(userDetails?.ContactId);
        setCourses(courseData || []);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setCoursesLoaded(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Distinct trainings (CourseName) across every batch the faculty teaches,
  // sorted A-Z (numeric-aware) so "the first training" is a stable,
  // predictable default rather than whatever order the API happened to
  // return.
  const courseNames = useMemo(
    () => Array.from(new Set(courses.map((c) => c.CourseName).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })),
    [courses]
  );

  // Once courses load, Training defaults to the deep-linked batch's
  // training (if any) or the first training — there's no "all trainings"
  // option, so one must always be selected.
  useEffect(() => {
    if (courseNames.length === 0 || selectedCourseName) return;
    const deepLinkedCourse = initialBatchId ? courses.find((c) => c.ProgramPlanId === initialBatchId) : undefined;
    setSelectedCourseName(deepLinkedCourse?.CourseName || courseNames[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseNames]);

  // The batch dropdown only offers batches for the chosen Training, sorted
  // ascending (numeric-aware) so "Batch 2" comes before "Batch 10".
  const coursesForSelectedCourseName = useMemo(
    () => courses
      .filter((c) => c.CourseName === selectedCourseName && c.ProgramPlanId)
      .sort((a, b) => a.TermName.localeCompare(b.TermName, undefined, { numeric: true, sensitivity: 'base' })),
    [courses, selectedCourseName]
  );

  // Switching Training resets the batch pick back to "All batches" if the
  // current pick no longer belongs to the chosen training, so the batch
  // dropdown never keeps a stale batch from a different training.
  useEffect(() => {
    if (selectedBatch === ALL_BATCHES) return;
    if (!coursesForSelectedCourseName.some((c) => c.ProgramPlanId === selectedBatch)) {
      setSelectedBatch(ALL_BATCHES);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coursesForSelectedCourseName]);

  const batchesToFetch = (selectedBatch === ALL_BATCHES
    ? coursesForSelectedCourseName
    : coursesForSelectedCourseName.filter((c) => c.ProgramPlanId === selectedBatch)
  ).map((c) => ({ batchId: c.ProgramPlanId as string, courseLabel: c.TermName || c.CourseName }));

  const fetchTopics = useCallback(async (selectFirst: boolean) => {
    if (!coursesLoaded) {
      setLoading(true);
      return;
    }
    if (batchesToFetch.length === 0) {
      setTopics([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const perBatch = await Promise.all(
        batchesToFetch.map(async ({ batchId, courseLabel }) => {
          try {
            const { pending, current, history } = await fetchDiscussionTopics(batchId);
            const topicsData: DiscussionTopic[] = [...pending, ...current, ...history];
            return topicsData.map((t): TopicWithCourse => ({ ...t, courseLabel, batchId }));
          } catch (batchErr) {
            return [] as TopicWithCourse[];
          }
        })
      );
      const topicsData = perBatch.flat();
      setTopics(topicsData);
      if (selectFirst) {
        const deepLinked = pendingDeepLinkTopicId.current;
        pendingDeepLinkTopicId.current = null;
        const deepLinkedExists = deepLinked && topicsData.some((t) => t.topicId === deepLinked);
        setSelectedTopicId(deepLinkedExists ? deepLinked : (topicsData.length > 0 ? topicsData[0].topicId : null));
      }
    } catch (error) {
      console.error('Error fetching discussion topics:', error);
      setTopics([]);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatch, selectedCourseName, courses, coursesLoaded]);

  useEffect(() => {
    fetchTopics(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBatch, selectedCourseName, courses, coursesLoaded]);

  const selectedTopic = topics.find((t) => t.topicId === selectedTopicId);

  if (loading) return <KenLoader />;

  const trainingOptions = courseNames.map((name) => ({ label: name, value: name }));

  const batchOptions = [
    { label: 'All batches', value: ALL_BATCHES },
    ...coursesForSelectedCourseName.map((c) => ({ label: c.TermName || c.CourseName, value: c.ProgramPlanId as string })),
  ];

  // "Post topic" needs one concrete batch to create against — pick the
  // batch the faculty is filtered to, or the selected topic's own batch as
  // a fallback once "All batches" has fetched at least one real topic.
  const postToBatchId = selectedBatch !== ALL_BATCHES ? selectedBatch : selectedTopic?.batchId;

  return (
    <Box className={classes.root}>
      <DiscussionLeftPanel
        topics={topics}
        loading={loading}
        selectedTopicId={selectedTopicId}
        onTopicClick={setSelectedTopicId}
        trainingOptions={trainingOptions}
        selectedCourseName={selectedCourseName}
        onCourseNameChange={setSelectedCourseName}
        batchOptions={batchOptions}
        selectedBatch={selectedBatch}
        onBatchChange={setSelectedBatch}
        courses={courses}
        postToBatchId={postToBatchId}
        onTopicCreated={() => fetchTopics(false)}
        currentUserName={currentUserName}
        showCourseTag={selectedBatch === ALL_BATCHES}
      />

      <DiscussionRightPanel
        selectedTopic={selectedTopic}
        contactId={userDetails?.ContactId}
        onCommentRated={() => fetchTopics(false)}
      />
    </Box>
  );
};

export default FacultyPortalDiscussion;
